import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight, Maximize2, Minimize2, BookOpen } from 'lucide-react';
import HTMLFlipBook from 'react-pageflip';

import { BACKEND_URL, API } from '../utils/api';
import { getToken, authFetch } from '../utils/session';

const ImagePage = React.forwardRef(({ src }, ref) => (
  <div ref={ref} className="w-full h-full" style={{ backgroundColor: '#f5f0eb' }}>
    <img src={src} alt="" className="w-full h-full object-cover" style={{ display: 'block' }} />
  </div>
));

const buildPageUrl = (slug, pageNum, token) => {
  const base = `${API}/books/${slug}/page/${pageNum}`;
  return token ? `${base}?token=${encodeURIComponent(token)}` : base;
};

const CtaPage = React.forwardRef(({ book, onBuy }, ref) => (
  <div ref={ref} className="w-full h-full flex flex-col items-center justify-center p-8 text-center bg-gradient-to-b from-background to-secondary/50">
    <div className="text-4xl mb-4">{'\u271D'}</div>
    <h3 className="text-lg font-bold mb-2">¿Te gustó lo que viste?</h3>
    <p className="text-sm text-muted-foreground mb-6 max-w-[200px]">Adquiere &quot;{book.title}&quot; para leer todas las páginas</p>
    <button onClick={(e) => { e.stopPropagation(); onBuy(); }} className="h-10 px-6 rounded-xl bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 transition-colors">
      Agregar al carrito · ${book.price?.toFixed(2)}
    </button>
  </div>
));

const EndCtaPage = React.forwardRef((props, ref) => (
  <div ref={ref} className="w-full h-full flex flex-col items-center justify-center p-8 text-center bg-gradient-to-b from-secondary/30 to-background">
    <p className="text-2xl mb-3">✨</p>
    <h3 className="text-lg font-bold mb-2">¡Qué bonito fue leer juntos!</h3>
    <p className="text-sm text-muted-foreground mb-6 max-w-[220px]">Tenemos muchas más historias de fe esperando por los más pequeños de tu hogar</p>
    <a href="/" className="h-10 px-6 rounded-xl bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 transition-colors inline-flex items-center">
      Descubrir más historias
    </a>
  </div>
));

const ImageFlipbook = ({ book, onClose, preview = false, onBuy, isGift = false }) => {
  const flipBookRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [pageRatio, setPageRatio] = useState(0.6471);
  const realPages = book.imagePages || book.pageCount || 0;
  const previewPages = Math.min(3, realPages);
  const showEndCta = !preview && !isGift;
  const totalPages = preview ? previewPages + 1 : realPages + (showEndCta ? 1 : 0);
  const token = getToken();
  const pagesToShow = preview ? previewPages : realPages;

  // Preload first image to detect real aspect ratio, then preload rest in background
  useEffect(() => {
    if (pagesToShow <= 0) { setReady(true); return; }
    const firstImg = new window.Image();
    firstImg.onload = () => {
      if (firstImg.naturalWidth && firstImg.naturalHeight) {
        setPageRatio(firstImg.naturalWidth / firstImg.naturalHeight);
      }
      setReady(true);
      for (let i = 2; i <= pagesToShow; i++) {
        const bg = new window.Image();
        bg.src = buildPageUrl(book.slug, i, token);
      }
    };
    firstImg.onerror = () => { setReady(true); };
    firstImg.src = buildPageUrl(book.slug, 1, token);
  }, [book.slug, token, pagesToShow]);

  // Calculate page dimensions from viewport — no DOM dependency
  const calcDims = useCallback(() => {
    const headerH = 48;
    const pad = 24;
    let maxH, maxW;
    if (isFullscreen) {
      maxH = window.innerHeight - headerH - pad;
      maxW = window.innerWidth - pad;
    } else {
      maxH = Math.min(window.innerHeight * 0.75, 600);
      maxW = Math.min(window.innerWidth * 0.9, 900) - pad;
    }
    let h = maxH;
    let w = Math.round(h * pageRatio);
    if (w > maxW) { w = maxW; h = Math.round(w / pageRatio); }
    return { w: Math.max(w, 150), h: Math.max(h, 230) };
  }, [isFullscreen, pageRatio]);

  const [bookDims, setBookDims] = useState(() => ({ w: 350, h: 540 }));
  useEffect(() => {
    const update = () => setBookDims(calcDims());
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [calcDims]);

  const onFlip = useCallback((e) => { setCurrentPage(e.data); }, []);
  const goNext = useCallback(() => { flipBookRef.current?.pageFlip()?.flipNext(); }, []);
  const goPrev = useCallback(() => { flipBookRef.current?.pageFlip()?.flipPrev(); }, []);
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) { containerRef.current?.requestFullscreen(); setIsFullscreen(true); }
    else { document.exitFullscreen(); setIsFullscreen(false); }
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      if (e.key === 'Escape') { if (document.fullscreenElement) { document.exitFullscreen(); setIsFullscreen(false); } else onClose(); }
    };
    window.addEventListener('keydown', handler);
    const onFs = () => { if (!document.fullscreenElement) setIsFullscreen(false); };
    document.addEventListener('fullscreenchange', onFs);
    return () => { window.removeEventListener('keydown', handler); document.removeEventListener('fullscreenchange', onFs); };
  }, [goNext, goPrev, onClose]);

  // Track read when opening a full book (not preview)
  useEffect(() => {
    if (!preview && book.slug) {
      authFetch(`${API}/books/${book.slug}/read`, { method: 'POST' }).catch(() => {});
    }
  }, [preview, book.slug]);

  if (!ready) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center" onClick={onClose}>
        <div className="bg-background rounded-2xl p-8 text-center max-w-xs" onClick={(e) => e.stopPropagation()}>
          <BookOpen className="size-8 mx-auto mb-4 text-foreground/60 animate-pulse" />
          <p className="text-sm font-medium text-foreground/80">Preparando libro...</p>
        </div>
      </div>
    );
  }

  const contentH = isFullscreen ? window.innerHeight - 48 : bookDims.h + 24;
  const contentW = isFullscreen ? window.innerWidth : bookDims.w * 2 + 48;

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center" onClick={onClose}>
      <div ref={containerRef} className={`bg-background shadow-2xl rounded-2xl flex flex-col overflow-hidden ${isFullscreen ? '!w-full !h-full !rounded-none' : ''}`} onClick={(e) => e.stopPropagation()} style={!isFullscreen ? { maxWidth: '95vw', width: contentW } : undefined}>
        <div className="flex items-center justify-between px-3 sm:px-4 h-[48px] shrink-0 border-b border-border/30">
          <div className="flex items-center gap-2">
            <button onClick={onClose} data-testid="flipbook-close-btn" className="text-foreground/60 hover:text-foreground transition-colors p-1.5 rounded-lg hover:bg-black/5"><X className="size-5" /></button>
            <div className="flex items-center gap-1.5 text-foreground/80"><BookOpen className="size-4 shrink-0" /><span data-testid="flipbook-title" className="text-xs sm:text-sm font-medium truncate max-w-[120px] sm:max-w-[200px]">{book.title}</span></div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={goPrev} disabled={currentPage === 0} data-testid="flipbook-prev-btn" className="text-foreground/40 hover:text-foreground disabled:text-foreground/15 transition-colors p-1"><ChevronLeft className="size-5" /></button>
            <span data-testid="flipbook-page-indicator" className="text-foreground/60 text-xs sm:text-sm font-medium min-w-[40px] text-center">{currentPage + 1}/{totalPages}</span>
            <button onClick={goNext} disabled={currentPage >= totalPages - 1} data-testid="flipbook-next-btn" className="text-foreground/40 hover:text-foreground disabled:text-foreground/15 transition-colors p-1"><ChevronRight className="size-5" /></button>
          </div>
          <button onClick={toggleFullscreen} data-testid="flipbook-fullscreen-btn" className="text-foreground/40 hover:text-foreground transition-colors p-1.5 rounded-lg hover:bg-black/5">
            {isFullscreen ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
          </button>
        </div>
        <div className="flex items-center justify-center overflow-hidden" style={{ height: contentH }}>
          <HTMLFlipBook
            key={`${bookDims.w}-${bookDims.h}`}
            ref={flipBookRef} width={bookDims.w} height={bookDims.h} size="fixed"
            showCover={true} mobileScrollSupport={true} onFlip={onFlip}
            className="[&_.stf__parent]:bg-transparent" style={{ background: 'transparent' }}
            startPage={0} drawShadow={true} flippingTime={700}
            usePortrait={true} maxShadowOpacity={0.4}
            showPageCorners={true} disableFlipByClick={false}>
            {preview ? [
              ...Array.from({ length: previewPages }, (_, i) => (
                <ImagePage key={i} src={buildPageUrl(book.slug, i + 1, token)} />
              )),
              <CtaPage key="cta" book={book} onBuy={onBuy || onClose} />,
            ] : [
              ...Array.from({ length: realPages }, (_, i) => (
                <ImagePage key={i} src={buildPageUrl(book.slug, i + 1, token)} />
              )),
              ...(showEndCta ? [<EndCtaPage key="end-cta" />] : []),
            ]}
          </HTMLFlipBook>
        </div>
      </div>
    </div>
  );
};

// --- Main FlipbookViewer: images first, PDF only if small, text fallback ---
const FlipbookViewer = ({ book, onClose, preview = false, onBuy, isGift = false }) => {
  const pages = book.imagePages || book.pageCount || 0;
  if (pages <= 0) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center" onClick={onClose}>
        <div className="bg-background rounded-2xl p-8 text-center max-w-sm" onClick={e => e.stopPropagation()}>
          <p className="text-sm text-muted-foreground mb-4">Este libro aún no tiene páginas disponibles</p>
          <button onClick={onClose} className="h-9 px-4 rounded-xl bg-foreground text-background text-sm font-medium">Cerrar</button>
        </div>
      </div>
    );
  }
  return <ImageFlipbook book={book} onClose={onClose} preview={preview} onBuy={onBuy} isGift={isGift} />;
};

export default FlipbookViewer;
